import { StrictMode } from "react";
import { createRoot } from "react-dom/client";
import { BrowserRouter } from "react-router-dom";
import { ClerkProvider } from "@clerk/clerk-react";
import App from "./App";

/*
 * Atkinson Hyperlegible carries the felt and every control. It was drawn by the
 * Braille Institute to keep characters distinguishable at speed, which is exactly the
 * requirement for a surface where a stack changes every three seconds. The display
 * serif stays on marketing and headings, where reading is slow and voice matters more.
 *
 * Self-hosted via fontsource rather than fetched from Google, so no visitor IP leaves
 * for a third party — the product ships a privacy policy and that claim should be
 * true without an asterisk.
 */
import "@fontsource/atkinson-hyperlegible/400.css";
import "@fontsource/atkinson-hyperlegible/700.css";
import "@fontsource/cormorant-garamond/600.css";
import "@fontsource/ibm-plex-mono/400.css";
import "@fontsource/ibm-plex-mono/500.css";
/**
 * RYE, for what is printed on the cloth.
 *
 * The only place in the app that sets Rye as text rather than carrying its outline as
 * a path — `lib/mark.tsx` does the latter, on purpose, so the brand's B never waits on
 * a webfont. This is different: it is the blackjack felt's silkscreened rules, and a
 * house rule printed on a table in a system sans reads as a caption rather than as
 * part of the table. Rye is the 1800s woodtype face the Frontier Book names on page
 * six, it is SIL Open Font Licence, and it is already a dependency.
 *
 * The face is chosen through `--felt-print` in `app.css`, so replacing it is one line.
 */
import "@fontsource/rye/400.css";
import "./app.css";

const pk = import.meta.env.VITE_CLERK_PUBLISHABLE_KEY as string | undefined;
const root = document.getElementById("root")!;

if (!pk) {
  // Fail loudly. A missing key otherwise renders a sign-in button that does nothing,
  // which looks like a bug in the app rather than a missing environment variable.
  root.innerHTML =
    '<p style="font:16px/1.5 system-ui;padding:2rem;color:#e5766b;max-width:38rem;margin:0 auto">' +
    "<strong>VITE_CLERK_PUBLISHABLE_KEY is not set.</strong><br>" +
    "Copy <code>.env.example</code> to <code>.env</code>, fill in the publishable key, " +
    "and rebuild. Vite bakes env vars in at build time, so a running dev server needs a restart." +
    "</p>";
} else {
  createRoot(root).render(
    <StrictMode>
      <BrowserRouter>
        <ClerkProvider publishableKey={pk}>
          <App />
        </ClerkProvider>
      </BrowserRouter>
    </StrictMode>
  );

  // The PWA shell was written in Phase 2 but never registered, so it had no effect.
  // Production only: in dev it would serve a stale bundle and hide your own changes.
  if (import.meta.env.PROD && "serviceWorker" in navigator) {
    window.addEventListener("load", () => {
      void navigator.serviceWorker.register("/sw.js").catch(() => {
        /* Offline support is a bonus; never let it break the app. */
      });
    });
  }
}
